import React from 'react';
import { useHalachicData } from '../../hooks/useHalachicData';
import templates from './templates';
import './TefilotWidget.css';

const toMinutes = (t) => {
  if (!t || t === '--:--') return -1;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const fromMinutes = (mins) =>
  `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

const expandSlots = (startStr, endStr, interval) => {
  const start = toMinutes(startStr);
  let end = toMinutes(endStr);
  if (start < 0 || end < 0) return [];
  // midnight wraparound: e.g. start=20:30 end=00:05
  if (end < start) end += 24 * 60;
  const iv = parseInt(interval) || 30;
  const slots = [];
  for (let t = start; t <= end; t += iv) slots.push(fromMinutes(t % (24 * 60)));
  return slots;
};

const DOW_TO_DAY_KEY = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const DAY_KEY_LABEL = { sun: 'א׳', mon: 'ב׳', tue: 'ג׳', wed: 'ד׳', thu: 'ה׳', fri: 'ו׳', sat: 'ש׳' };
const DAY_KEY_SHORT = { sun: 'א', mon: 'ב', tue: 'ג', wed: 'ד', thu: 'ה', fri: 'ו', sat: 'ש' };
const ALL_TEFILOT_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri'];

const formatDaysLabel = (days) => {
  if (!days?.length) return null;
  const ordered = ALL_TEFILOT_KEYS.filter(k => days.includes(k));
  if (ordered.length === 0) return null;
  // Build consecutive runs
  const runs = [];
  let start = 0;
  for (let i = 1; i <= ordered.length; i++) {
    const prevIdx = ALL_TEFILOT_KEYS.indexOf(ordered[i - 1]);
    const curIdx = i < ordered.length ? ALL_TEFILOT_KEYS.indexOf(ordered[i]) : -1;
    if (curIdx !== prevIdx + 1) {
      runs.push(ordered.slice(start, i));
      start = i;
    }
  }
  return runs.map(run =>
    run.length >= 3
      ? `${DAY_KEY_SHORT[run[0]]}׳–${DAY_KEY_SHORT[run[run.length - 1]]}׳`
      : run.map(k => DAY_KEY_LABEL[k]).join(' ')
  ).join(', ');
};

export const TefilotWidget = ({
  widgetId,
  // Legacy props (no longer used)
  tefilotData: legacyTefilotData,
  loading: legacyLoading,
  error: legacyError,
  dispatch: legacyDispatch,
  location,
  customTimes = [],
  template = 'Modern',
  title = 'תפילות יום חול',
  showTitle = true,
  recurringDisplay = 'compact',
  showRuleMode = false,
  highlightNextMinyan = false,
}) => {
  // ── שליפה מהמנוע ההלכתי ──────────────────────────────────────────────
  const engineConfig = {
    location: {
      lat: location?.lat,
      lng: location?.lng,
      timezone: location?.timezone || location?.tzid || 'Asia/Jerusalem',
    },
    isIsrael: true,
    edah: 'ashkenaz',
  };

  const { today, loading: engineLoading, error: engineError } = useHalachicData(engineConfig);

  const zmanim = today?.zmanim;

  const getBaseTimeValue = (key) => {
    if (!zmanim) return null;
    const map = {
      daySunset: zmanim.sunset,
      dayTzeit7083: zmanim.tzeit7083deg,
      dayTzeit85: zmanim.tzeit85deg,
      dayTzeit42: zmanim.tzeit42min,
      dayTzeit50: zmanim.tzeit50min,
      dayTzeit72: zmanim.tzeit72min,
      dayAlot: zmanim.alotHaShachar,
      dayNetz: zmanim.netzHachama,
      dayChatzot: zmanim.chatzot,
      dayChatzotNight: zmanim.chatzotNight,
      daySofZmanShma: zmanim.sofZmanShma,
      daySofZmanShmaMGA: zmanim.sofZmanShmaMGA,
      daySofZmanTfilla: zmanim.sofZmanTfilla,
      daySofZmanTfillaMGA: zmanim.sofZmanTfillaMGA,
      dayMinchaGedola: zmanim.minchaGedola,
      dayMinchaKetana: zmanim.minchaKetana,
      dayPlagHamincha: zmanim.plagHamincha,
      daySofZmanAchilatChametz: zmanim.sofZmanAchilatChametz,
      daySofZmanBiurChametz: zmanim.sofZmanBiurChametz,
      daySofZmanAchilatChametzMGA: zmanim.sofZmanAchilatChametzMGA,
      daySofZmanBiurChametzMGA: zmanim.sofZmanBiurChametzMGA,
    };
    return map[key] || null;
  };

  const calculateRelativeTime = (baseTime, offset, roundTo) => {
    if (!baseTime) return '--:--';
    try {
      let timeStr = baseTime;
      if (baseTime.includes('T')) {
        timeStr = new Date(baseTime).toTimeString().slice(0, 5);
      } else if (baseTime.match(/^\d{2}:\d{2}:\d{2}$/)) {
        timeStr = baseTime.slice(0, 5);
      } else if (!baseTime.match(/^\d{2}:\d{2}$/)) {
        const d = new Date(baseTime);
        if (!isNaN(d.getTime())) {
          timeStr = d.toTimeString().slice(0, 5);
        } else {
          return '--:--';
        }
      }
      const [hours, minutes] = timeStr.split(':').map(Number);
      let totalMinutes = hours * 60 + minutes + (parseInt(offset) || 0);
      let finalMinutes = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
      if (roundTo && roundTo > 0) {
        const mins = finalMinutes % 60;
        finalMinutes = Math.floor(finalMinutes / 60) * 60 + Math.floor(mins / roundTo) * roundTo;
      }
      return fromMinutes(finalMinutes);
    } catch {
      return '--:--';
    }
  };

  const resolveDescriptor = (desc) => {
    if (!desc) return '--:--';
    if (desc.type === 'fixed') return desc.time || '--:--';
    if (desc.type === 'relative') {
      return calculateRelativeTime(getBaseTimeValue(desc.baseTime), desc.offset, desc.roundTo);
    }
    return '--:--';
  };

  const processedCustomTimes = customTimes.map(ct => {
    if (ct.type === 'recurring') {
      const startDesc = ct.startType === 'relative'
        ? { type: 'relative', baseTime: ct.startBaseTime, offset: ct.startOffset, roundTo: ct.startRoundTo }
        : { type: 'fixed', time: ct.startTime };
      const endDesc = ct.endType === 'relative'
        ? { type: 'relative', baseTime: ct.endBaseTime, offset: ct.endOffset, roundTo: ct.endRoundTo }
        : { type: 'fixed', time: ct.endTime };

      const resolvedStart = resolveDescriptor(startDesc);
      const resolvedEnd = resolveDescriptor(endDesc);
      const resolvedSlots = expandSlots(resolvedStart, resolvedEnd, ct.interval);

      // Resolve override times
      const resolvedOverrides = (ct.dayOverrides || []).map(ov => ({
        ...ov,
        resolvedTime: resolveDescriptor({ type: ov.type, time: ov.time, baseTime: ov.baseTime, offset: ov.offset, roundTo: ov.roundTo })
      }));

      return { ...ct, resolvedStart, resolvedEnd, resolvedSlots, resolvedOverrides };
    }

    // fixed or relative
    const resolvedTime = ct.type === 'fixed'
      ? (ct.time || '--:--')
      : calculateRelativeTime(getBaseTimeValue(ct.baseTime), ct.offset, ct.roundTo);

    const resolvedOverrides = (ct.dayOverrides || []).map(ov => ({
      ...ov,
      resolvedTime: resolveDescriptor({ type: ov.type, time: ov.time, baseTime: ov.baseTime, offset: ov.offset, roundTo: ov.roundTo })
    }));

    return { ...ct, resolvedTime, resolvedOverrides };
  }).sort((a, b) => {
    const PRAYER_ORDER = { shacharit: 0, mincha: 1, maariv: 2 };
    const pA = PRAYER_ORDER[a.prayer] ?? 3;
    const pB = PRAYER_ORDER[b.prayer] ?? 3;
    if (pA !== pB) return pA - pB;
    // For maariv/unknown types: times before 06:00 are after-midnight → sort last
    // Shacharit/mincha never cross midnight so no adjustment needed
    const normForSort = (mins, prayer) => {
      if (mins < 0 || prayer === 'shacharit' || prayer === 'mincha') return mins;
      return mins < 4 * 60 ? mins + 24 * 60 : mins;
    };
    const tA = normForSort(a.type === 'recurring' ? toMinutes(a.resolvedStart) : toMinutes(a.resolvedTime), a.prayer);
    const tB = normForSort(b.type === 'recurring' ? toMinutes(b.resolvedStart) : toMinutes(b.resolvedTime), b.prayer);
    if (tA < 0 && tB < 0) return 0;
    if (tA < 0) return 1;
    if (tB < 0) return -1;
    return tA - tB;
  });

  // Annotate prayers with day info for display
  const todayKey = DOW_TO_DAY_KEY[new Date().getDay()];
  const annotatedCustomTimes = processedCustomTimes.map(ct => {
    const hasDays = ct.days?.length > 0;
    const isActiveToday = !hasDays || ct.days.includes(todayKey);
    const isPartialDays = hasDays && ct.days.length < ALL_TEFILOT_KEYS.length;
    return {
      ...ct,
      daysLabel: isPartialDays ? formatDaysLabel(ct.days) : null,
      isActiveToday,
    };
  });

  const Template = templates[template] || templates['Modern'];

  if (engineLoading && !today) {
    return (
      <Template
        processedCustomTimes={[]}
        loading={true}
        error={null}
        title={title}
        showTitle={showTitle}
        recurringDisplay={recurringDisplay}
        showRuleMode={showRuleMode}
        highlightNextMinyan={highlightNextMinyan}
      />
    );
  }

  return (
    <Template
      processedCustomTimes={annotatedCustomTimes}
      loading={false}
      error={engineError && !today ? 'שגיאה בטעינת הנתונים' : null}
      title={title}
      showTitle={showTitle}
      recurringDisplay={recurringDisplay}
      showRuleMode={showRuleMode}
      highlightNextMinyan={highlightNextMinyan}
    />
  );
};
